import { ChangeEvent, useCallback, useEffect, useRef, useState } from 'react';

import {
  Button,
  Center,
  Flex,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Spinner,
  Text,
  useBoolean,
  useDisclosure,
} from '@chakra-ui/react';

import { useForm } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';

import { useQueryClient } from '@tanstack/react-query';

import {
  buildError,
  bulkImportFiles,
  BulkImportStatus,
  textStyles,
  useDownload,
  CustomAlert,
  shadowStyles,
  useToast,
  BookOutline,
  DocumentDownloadOutline,
  DocumentUploadOutline,
  RefreshOutline,
  ValidationErrorTables,
  FileValidationError,
} from '@aslanpay/portal-common-lib';

import {
  ImportedEmployeeColumns,
  ImportedAETColumns,
  GiftingImportedEmployeeColumns,
} from 'app/constants/table';

import useReadImportCSV from 'app/hooks/useReadImportCSV';

// AET File Processing Service hooks
import { useUploadAETFile } from 'app/hooks/useAETFileHooks';

// EDF File Processing Service hooks
import { useUploadEDFFile } from 'app/hooks/useEDFFileHooks';

import { useImportEmployeesStore } from 'app/store';

import { importEmployeesUploadFormSchema } from 'app/utils/schemas';

import { useParams } from 'react-router-dom';
import useFetchCompany from 'app/hooks/useFetchCompany';

import { ApprovedEarningsTimesheetResponse, EdfUploadType } from 'app/types/fileProcessingService';
import { UploadedEDFFile } from 'app/hooks/useUploadEDFFile';

import appEnv from 'app/config/appEnv';

import {
  ImportedEmployeeCSV,
  ImportedAETCSV,
  ImportEmployeesStepProps,
  UploadFileFormFields,
} from '../types';

import InstructionsModal from '../InstructionsModal/InstructionsModal';

const S3_PUBLIC_BUCKET_URL = appEnv.VITE_S3_BUCKET_URI;

// File upload statuses that indicate the fps upload/analysis has reached a terminal (or
// confirmation-ready) state and the wizard can move on to the confirm step.
const TERMINAL_UPLOAD_STATUSES = [
  'AWAITING_CLIENT_CONFIRMATION',
  'VALIDATED',
  'SUCCEEDED',
  'PARTIALLY_SUCCEEDED',
  'FAILED',
  'VALIDATION_FAILED',
];

function UploadFile({ id, onStepAction }: ImportEmployeesStepProps) {
  const queryClient = useQueryClient();
  const download = useDownload();
  const toast = useToast();

  const { companyId } = useParams();
  const { importEmployeesData, setImportEmployeesData } = useImportEmployeesStore.getState();

  // Use company ID from URL params (company view) or the store (global view)
  const effectiveCompanyId = companyId || importEmployeesData.companyId;
  const company = useFetchCompany(effectiveCompanyId);

  const [isDraggingFileOver, setIsDraggingFileOver] = useBoolean(false);

  // State to track the uploaded AET file
  const [uploadedAETFile, setUploadedAETFile] = useState<ApprovedEarningsTimesheetResponse | null>(
    null
  );

  // State to track the uploaded EDF file
  const [uploadedEDFFile, setUploadedEDFFile] = useState<UploadedEDFFile | null>(null);

  // Structured row/field-level validation errors from fps's analyse step (e.g. missing
  // employees, bad rows) — covers both standard and gifting-only EDF uploads.
  const [edfErrors, setEdfErrors] = useState<FileValidationError[]>([]);
  const edfErrorsRef = useRef<HTMLDivElement>(null);

  // Get the selected file type for context-aware UI
  const fileType = importEmployeesData.fileType || 'EMPLOYEE_DATA_FILE';
  const isEDF = fileType === 'EMPLOYEE_DATA_FILE' || fileType === 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';
  const isStandardEdf = fileType === 'EMPLOYEE_DATA_FILE';
  const isGiftingEDF = fileType === 'GIFTING_ONLY_EMPLOYEE_DATA_FILE';
  const isAET = fileType === 'APPROVED_EARNINGS_TIMESHEET';

  // Context-aware CSV parsing
  let columns;
  if (isStandardEdf) columns = Object.values(ImportedEmployeeColumns);
  else if (isGiftingEDF) columns = Object.values(GiftingImportedEmployeeColumns);
  else columns = Object.values(ImportedAETColumns);

  const { parsedData, parseCSV, isParsingCSV, resetParsedData } = useReadImportCSV<
    ImportedEmployeeCSV | ImportedAETCSV
  >(columns, fileType);

  const {
    setValue,
    watch,
    formState: { isValid },
  } = useForm<UploadFileFormFields>({
    mode: 'onBlur',
    defaultValues: {
      bulkImportId: '',
      bulkImportStatus: '',
    },
    resolver: yupResolver(importEmployeesUploadFormSchema),
  });

  const {
    isOpen: isInstructionsOpen,
    onOpen: onOpenInstructions,
    onClose: onCloseInstructions,
  } = useDisclosure();

  const handleOpenInstructionsModal = () => {
    onOpenInstructions();
  };

  const handleDownloadTemplate = () => {
    let fileUrl: string;
    let fileName: string;

    if (fileType === 'APPROVED_EARNINGS_TIMESHEET') {
      fileUrl = bulkImportFiles.IMPORT_AET_EMPLOYEES;
      fileName = 'AET Timesheet Template.csv';
    } else if (company.data?.type === 'GIFTING_ONLY') {
      fileUrl = bulkImportFiles.IMPORT_GIFTING_EMPLOYEES;
      fileName = 'Import Employees.csv';
    } else {
      fileUrl = bulkImportFiles.IMPORT_STANDARD_EMPLOYEES;
      fileName = 'Import Employees.csv';
    }

    const templateUrl = `${S3_PUBLIC_BUCKET_URL}${fileUrl}`;
    download(templateUrl, fileName);
  };

  // AET-specific hooks (Approved Earnings Timesheet)
  const edfFile = uploadedEDFFile;
  const isLatestEDFFileProcessing =
    !!edfFile && !TERMINAL_UPLOAD_STATUSES.includes(edfFile.fileUpload?.status || '');

  const {
    mutate: uploadAETFile,
    isLoading: isUploadingAETFile,
    reset: resetAETUploadMutation,
  } = useUploadAETFile({
    onSuccess: (response) => {
      // Store the uploaded file details - response.fileUpload is actually the full ApprovedEarningsTimesheetResponse
      setUploadedAETFile(response.fileUpload);
      // Also store in global store for ConfirmData component
      const currentData = useImportEmployeesStore.getState().importEmployeesData;
      setImportEmployeesData({
        ...currentData,
        uploadedAETFile: response.fileUpload,
      });
      queryClient.invalidateQueries({ queryKey: ['fetchAETFiles'] });
    },
    onError: (error) => {
      const errorMessage = buildError(
        error,
        'Sorry, it was not possible to upload your timesheet file. Please try again.'
      );

      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: errorMessage,
      });
    },
  });

  const {
    mutate: uploadEDFFile,
    isLoading: isUploadingEDFFile,
    reset: resetEDFUploadMutation,
  } = useUploadEDFFile({
    onSuccess: (response) => {
      setUploadedEDFFile(response.fileUpload);
      const currentData = useImportEmployeesStore.getState().importEmployeesData;
      setImportEmployeesData({
        ...currentData,
        uploadedEDFFile: response.fileUpload,
      });
    },
    onError: (error) => {
      const errorMessage = buildError(
        error,
        'Sorry, it was not possible to upload your employee file. Please try again.'
      );

      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: errorMessage,
      });
    },
    onValidationError: (errors) => {
      setEdfErrors(errors);
    },
  });

  // Debug logging for uploaded file
  const isLatestAETFileProcessing =
    uploadedAETFile?.fileUpload?.status === 'UPLOAD_REQUESTED' ||
    uploadedAETFile?.fileUpload?.status === 'ANALYSIS_IN_PROGRESS' ||
    uploadedAETFile?.fileUpload?.status === 'CLIENT_CONFIRMED' ||
    uploadedAETFile?.fileUpload?.status === 'VALIDATION_IN_PROGRESS' ||
    uploadedAETFile?.fileUpload?.status === 'PROCESSING_IN_PROGRESS';

  const aetFile = uploadedAETFile; // Use uploadedAETFile directly

  // Context-aware data based on file type
  const isProcessing = isEDF ? isLatestEDFFileProcessing : isLatestAETFileProcessing;
  const isUploading = isEDF ? isUploadingEDFFile : isUploadingAETFile;

  const isLoadingFiles = false;

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];

    if (uploadedFile) {
      // Always reset states when a new file is selected - this ensures clean slate
      setUploadedAETFile(null);
      setUploadedEDFFile(null);
      setEdfErrors([]);

      // Reset global store state
      const currentData = useImportEmployeesStore.getState().importEmployeesData;
      setImportEmployeesData({
        ...currentData,
        uploadedAETFile: undefined,
        uploadedEDFFile: undefined,
        bulkImportId: '',
        bulkImportStatus: undefined,
      });

      // Reset form values
      setValue('bulkImportId', '');
      setValue('bulkImportStatus', '');

      // Reset upload mutation state to ensure clean upload
      if (isAET && resetAETUploadMutation) {
        resetAETUploadMutation();
      }
      if (isEDF && resetEDFUploadMutation) {
        resetEDFUploadMutation();
      }

      // Parse the new file (this will trigger upload)
      parseCSV(uploadedFile);
    }
  };

  const handleNextStep = useCallback(
    ({ bulkImportId, bulkImportStatus }: UploadFileFormFields) => {
      setImportEmployeesData({
        bulkImportId,
        bulkImportStatus: bulkImportStatus as BulkImportStatus,
      });
      onStepAction();
    },
    [onStepAction, setImportEmployeesData]
  );

  // Reset state when component mounts or when going back to upload
  useEffect(() => {
    // Reset uploaded file state
    setUploadedAETFile(null);
    setUploadedEDFFile(null);
    setEdfErrors([]);

    // Reset parsed data
    resetParsedData();

    // Reset form values
    setValue('bulkImportId', '');
    setValue('bulkImportStatus', '');

    // Reset global store state to ensure clean slate
    const currentData = useImportEmployeesStore.getState().importEmployeesData;
    if (currentData.uploadedAETFile || currentData.uploadedEDFFile) {
      setImportEmployeesData({
        ...currentData,
        uploadedAETFile: undefined,
        uploadedEDFFile: undefined,
        bulkImportId: '',
        bulkImportStatus: undefined,
      });
    }

    // Reset upload mutation state
    if (isAET && resetAETUploadMutation) {
      resetAETUploadMutation();
    }
    if (isEDF && resetEDFUploadMutation) {
      resetEDFUploadMutation();
    }

    return () => {
      queryClient.resetQueries(['fetchAETFiles']);
      queryClient.resetQueries(['fetchAETFile']);
    };
  }, [
    queryClient,
    setValue,
    resetParsedData,
    setImportEmployeesData,
    isAET,
    isEDF,
    resetAETUploadMutation,
    resetEDFUploadMutation,
  ]);

  // Additional effect to reset when store state is cleared by parent component
  useEffect(() => {
    const currentData = useImportEmployeesStore.getState().importEmployeesData;

    // If the store has been reset (no uploaded file and no bulkImportId), reset local state too
    if (
      !currentData.uploadedAETFile &&
      !currentData.uploadedEDFFile &&
      !currentData.bulkImportId &&
      !currentData.bulkImportStatus
    ) {
      setUploadedAETFile(null);
      setUploadedEDFFile(null);
      setEdfErrors([]);
      setValue('bulkImportId', '');
      setValue('bulkImportStatus', '');

      // Reset upload mutation state
      if (isAET && resetAETUploadMutation) {
        resetAETUploadMutation();
      }
      if (isEDF && resetEDFUploadMutation) {
        resetEDFUploadMutation();
      }
    }
  }, [
    importEmployeesData.uploadedAETFile,
    importEmployeesData.uploadedEDFFile,
    importEmployeesData.bulkImportId,
    importEmployeesData.bulkImportStatus,
    setValue,
    isAET,
    isEDF,
    resetAETUploadMutation,
    resetEDFUploadMutation,
  ]);

  useEffect(() => {
    const { file } = parsedData;

    if (file) {
      if (isEDF) {
        uploadEDFFile({
          companyId: effectiveCompanyId || '',
          payCalendarId: importEmployeesData.payCalendarId || '',
          uploadType: fileType as EdfUploadType,
          file,
        });
      } else if (isAET) {
        const { metadata } = parsedData;
        uploadAETFile({
          companyId: effectiveCompanyId || '',
          payCalendarId: metadata?.payCalendarId || importEmployeesData.payCalendarId || '',
          file,
          metadata: metadata
            ? {
                payCalendarId: metadata.payCalendarId,
                startDate: metadata.startDate,
                endDate: metadata.endDate,
              }
            : undefined,
        });
      }
    } else if (parsedData.error) {
      // eslint-disable-next-line no-console
      console.error('❌ File Parsing Failed:', parsedData.error.message);
    }
  }, [
    importEmployeesData.companyId,
    importEmployeesData.payCalendarId,
    parsedData,
    uploadAETFile,
    uploadEDFFile,
    isEDF,
    isAET,
    fileType,
    effectiveCompanyId,
  ]);

  // Handle EDF processed files
  useEffect(() => {
    if (
      isEDF &&
      uploadedEDFFile?.fileUpload &&
      TERMINAL_UPLOAD_STATUSES.includes(uploadedEDFFile.fileUpload.status)
    ) {
      setValue('bulkImportId', uploadedEDFFile.fileUpload.id, { shouldValidate: true });
      setValue('bulkImportStatus', uploadedEDFFile.fileUpload.status as BulkImportStatus, {
        shouldValidate: true,
      });

      if (
        uploadedEDFFile.fileUpload.status === 'FAILED' ||
        uploadedEDFFile.fileUpload.status === 'VALIDATION_FAILED'
      ) {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'File processing failed',
          description:
            'Your employee data file could not be processed. Please check the file format and try again.',
        });
      }
    }
  }, [isEDF, uploadedEDFFile, setValue, toast]);

  // Handle AET processed files - consolidated logic
  useEffect(() => {
    if (
      isAET &&
      uploadedAETFile?.fileUpload &&
      (uploadedAETFile.fileUpload.status === 'AWAITING_CLIENT_CONFIRMATION' ||
        uploadedAETFile.fileUpload.status === 'VALIDATED' ||
        uploadedAETFile.fileUpload.status === 'SUCCEEDED' ||
        uploadedAETFile.fileUpload.status === 'FAILED' ||
        uploadedAETFile.fileUpload.status === 'VALIDATION_FAILED')
    ) {
      setValue('bulkImportId', uploadedAETFile.fileUpload.id, { shouldValidate: true });
      setValue('bulkImportStatus', uploadedAETFile.fileUpload.status as BulkImportStatus, {
        shouldValidate: true,
      });

      // Show error toast for failed statuses
      if (
        uploadedAETFile.fileUpload.status === 'FAILED' ||
        uploadedAETFile.fileUpload.status === 'VALIDATION_FAILED'
      ) {
        toast({
          position: 'top-right',
          status: 'error',
          title: 'File processing failed',
          description:
            'Your timesheet file could not be processed. Please check the file format and try again.',
        });
      }
    }
  }, [isAET, uploadedAETFile, setValue, toast]);

  useEffect(() => {
    if (edfErrors.length > 0) {
      edfErrorsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [edfErrors]);

  const formValues = watch();

  useEffect(() => {
    // Only attempt to submit when form is valid and we have the required values
    if (isValid && formValues.bulkImportId && formValues.bulkImportStatus) {
      // Call handleNextStep directly with the current form values
      handleNextStep({
        file: null, // File is not needed for this step
        bulkImportId: formValues.bulkImportId,
        bulkImportStatus: formValues.bulkImportStatus,
      });
    }
  }, [handleNextStep, isValid, formValues.bulkImportId, formValues.bulkImportStatus]);

  const getUploadingMessage = () => {
    if (isParsingCSV) {
      return 'Preparing your file for upload...';
    }

    if (isUploading) {
      return isEDF
        ? 'Uploading your employee file to our servers...'
        : 'Uploading your timesheet file to our servers...';
    }

    if (isEDF && isProcessing) {
      return "Hold tight, we're validating the employee data...";
    }

    if (isAET) {
      if (
        aetFile?.fileUpload?.status === 'UPLOAD_REQUESTED' ||
        aetFile?.fileUpload?.status === undefined
      ) {
        return 'Waiting to be processed...';
      }

      if (
        aetFile?.fileUpload?.status === 'ANALYSIS_IN_PROGRESS' ||
        aetFile?.fileUpload?.status === 'VALIDATION_IN_PROGRESS' ||
        aetFile?.fileUpload?.status === 'PROCESSING_IN_PROGRESS'
      ) {
        return "Hold tight, we're validating the timesheet data...";
      }
    }

    return 'Uploading file...';
  };

  const renderUploadingContainer = () => {
    return (
      <>
        <Flex
          position="relative"
          py="96px"
          flexDir="column"
          gap="15px"
          align="center"
          justify="center"
          bgColor="brand.purpleAlpha10"
          border="1px dashed"
          borderColor="brand.purple"
          borderRadius="5px"
        >
          <Flex gap="10px" color="brand.purple" align="center">
            <RefreshOutline boxSize="24px" animation="rotation 2s linear infinite" />
            <Text textStyle="h6SemiBold">{getUploadingMessage()}</Text>
          </Flex>
        </Flex>
        <Text alignSelf="center" color="brand.mutedPurple" textStyle="smallMedium">
          Please do not leave this screen or close the tab
        </Text>
      </>
    );
  };

  const renderUploadContainer = () => {
    return (
      <>
        <Text color="brand.mutedPurple" textStyle="smallMedium">
          {fileType === 'APPROVED_EARNINGS_TIMESHEET'
            ? 'Please drag and drop or upload your approved earnings timesheet file into the box below. If you have not set up your timesheet file yet, you can always download the template above.'
            : 'Please drag and drop or upload your employee file into the box below. If you have not set up your employee file yet, you can always download the template above.'}
        </Text>
        <CustomAlert
          variant="subtle"
          status="warning"
          title={
            fileType === 'APPROVED_EARNINGS_TIMESHEET'
              ? 'The timesheet template structure must not be changed.'
              : 'The employee template structure must not be changed.'
          }
          boxShadow="none"
        />
        <Flex
          position="relative"
          py="42px"
          flexDir="column"
          gap="15px"
          align="center"
          justify="center"
          bgColor={isDraggingFileOver ? 'brand.lightGray1' : 'brand.purpleAlpha10'}
          border="1px dashed"
          borderColor="brand.purple"
          borderRadius="5px"
          transition="background-color 0.2s ease-in-out"
        >
          <Input
            key={`file-input-${fileType}-${effectiveCompanyId}-${
              uploadedAETFile || uploadedEDFFile ? 'uploaded' : 'empty'
            }`}
            title=""
            variant="file"
            type="file"
            accept=".csv"
            onChange={(event) => {
              handleFileChange(event);
            }}
            onDragOver={setIsDraggingFileOver.on}
            onDragLeave={setIsDraggingFileOver.off}
            onDrop={setIsDraggingFileOver.off}
          />
          <Flex gap="10px" color="brand.purple" align="center">
            <DocumentUploadOutline boxSize="24px" />
            <Text textStyle="h6SemiBold">
              {fileType === 'APPROVED_EARNINGS_TIMESHEET'
                ? 'Upload the timesheet file'
                : 'Upload the updated employee file'}
            </Text>
          </Flex>

          <Text color="brand.mutedPurple" textStyle="smallRegular">
            {isDraggingFileOver ? 'Drop the file' : 'Drag & drop or browse'}
          </Text>
        </Flex>
      </>
    );
  };

  // Removed complex isUploadingFile logic - now using simple conditions
  const getTitleText = () => {
    if (isAET) return 'Upload timesheet file';
    if (isGiftingEDF) return 'Upload file';
    return 'Upload employee file';
  };

  const getDownloadText = () => {
    if (isAET) return 'Download timesheet template';
    if (isGiftingEDF) return 'Download template';
    return 'Download employee template';
  };

  return isLoadingFiles ? (
    <Center width="full" height="full" flexGrow={1}>
      <Spinner color="brand.purple" size="xl" />
    </Center>
  ) : (
    <>
      <InstructionsModal isOpen={isInstructionsOpen} onClose={onCloseInstructions} />
      <Flex
        flexDir="column"
        maxW={isEDF && edfErrors.length > 0 ? 'full' : '572px'}
        w="full"
        alignSelf="center"
        gap="20px"
        borderRadius="10px"
        boxShadow={shadowStyles.dropdownMenu}
      >
        <form id={id} noValidate>
          <FormControl isRequired>
            <HStack
              px="40px"
              py="30px"
              borderBottom="1px solid"
              borderColor="brand.lightGray1"
              alignItems="center"
              justify="space-between"
            >
              <FormLabel as="h5" sx={{ ...textStyles.h5Bold }} margin="0">
                {getTitleText()}
              </FormLabel>
              <HStack spacing="16px" align="center">
                <Button
                  size="sm"
                  variant="link"
                  leftIcon={<DocumentDownloadOutline boxSize="20px" />}
                  onClick={handleDownloadTemplate}
                >
                  {getDownloadText()}
                </Button>
                {isGiftingEDF && (
                  <Button
                    size="sm"
                    variant="link"
                    leftIcon={<BookOutline boxSize="20px" />}
                    onClick={handleOpenInstructionsModal}
                  >
                    Instructions
                  </Button>
                )}
              </HStack>
            </HStack>
            <Flex flexDir="column" px="40px" py="20px" gap="20px">
              {isParsingCSV || isUploading || (isEDF && isProcessing)
                ? renderUploadingContainer()
                : renderUploadContainer()}
              {isEDF && edfErrors.length > 0 && (
                <ValidationErrorTables errors={edfErrors} containerRef={edfErrorsRef} />
              )}
              <Text alignSelf="flex-start" color="system.error" textStyle="extraSmallMedium">
                * mandatory
              </Text>
            </Flex>
          </FormControl>
        </form>
      </Flex>
    </>
  );
}

export default UploadFile;
